import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from './stores/authStore';
import Sidebar from './components/Layout/Sidebar';
// import Header from './components/Layout/Header';
import Dashboard from './pages/Dashboard';
import Authentication from './pages/Authentication';
import Forms from './pages/Forms';
import Sales from './pages/Sales';
import StarlinkMonitor from './pages/StarlinkMonitor';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import SuperAdminRoute from './components/Auth/SuperAdminRoute';
import SalesRoute from './components/Auth/SalesRoute';
import MonitorRoute from './components/Auth/MonitorRoute';
import FormsRoute from './components/Auth/FormsRoute';
import DataTransferRoute from './components/Auth/DataTransferRoute';
import Users from './pages/Users';
import AdminRoute from './components/Auth/AdminRoute';
import Packages from './pages/Packages';
import AuditTrail from './pages/AuditTrail';
import DataUsage from './pages/DataUsage';
import DataUsageExports from './pages/DataUsageExports';
import Migrations from './pages/Migrations';
import MigrationHistory from './pages/MigrationHistory';
import PowerBI from './pages/PowerBI';
import Xero from './pages/Xero';
import EmailManagement from './pages/EmailManagement';
import LoginAudits from './pages/LoginAudits';
import Usage100Percent from './pages/Usage100Percent';
import DataTransfer from './pages/DataTransfer';
import ImpactPlan from './pages/ImpactPlan';
import ImpactPlanDetails from './pages/ImpactPlanDetails';
import ImpactPlanRoute from './components/Auth/ImpactPlanRoute';
import ServiceAccounts from './pages/ServiceAccounts';
import UsageEvents from './pages/UsageEvents';
import DeveloperPortal from './pages/DeveloperPortal';
import FormWorkspace from './pages/FormWorkspace';
import AuraLensPanel from './components/Common/AuraLensPanel';
import api, { authAPI, monitorAPI } from './services/api';
import { getAuraLensRouteMatch } from './auraLens/routeConfig';

const AI_INTELLIGENCE_ENABLED = false;

const App: React.FC = () => {
  const { isAuthenticated, user, token, refreshUser } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [authBootstrapComplete, setAuthBootstrapComplete] = React.useState(false);
  // Tracks whether we've already validated the CURRENT login session, not
  // which token we last saw — a routine background token refresh (every 15
  // minutes, or whenever the tab regains focus) produces a new token string
  // every time, and comparing against that would wrongly treat every
  // refresh as a brand-new session to restore, which unmounts the whole app
  // (see the loading gate below) and drops whatever the user was doing.
  const hasBootstrappedRef = React.useRef(false);

  // State to handle sidebar toggle on small screens
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Get backend API URL for Swagger docs redirect
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';
  const SWAGGER_URL = `${API_BASE_URL}/docs`;
  const auraLensSessionKey = React.useMemo(() => {
    if (!token) {
      return 'anonymous';
    }

    let hash = 0;
    for (let index = 0; index < token.length; index += 1) {
      hash = (hash * 31 + token.charCodeAt(index)) >>> 0;
    }

    return `token-${hash.toString(16)}`;
  }, [token]);
  const auraLensRoute = React.useMemo(
    () => (isAuthenticated ? getAuraLensRouteMatch(location.pathname) : null),
    [isAuthenticated, location.pathname]
  );
  React.useEffect(() => {
    if (!isAuthenticated || !token) {
      hasBootstrappedRef.current = false;
      setAuthBootstrapComplete(true);
      return;
    }

    if (hasBootstrappedRef.current) {
      // Already validated this session — a background token refresh
      // shouldn't re-trigger the "restoring session" gate.
      return;
    }

    hasBootstrappedRef.current = true;
    setAuthBootstrapComplete(false);

    void refreshUser().finally(() => {
      setAuthBootstrapComplete(true);
    });
  }, [isAuthenticated, refreshUser, token]);

  // Periodic background token refresh so users don't get booted while working
  React.useEffect(() => {
    if (!isAuthenticated || !token) return;

    const doRefresh = () => {
      api
        .post('/api/auth/token/refresh')
        .then((res) => {
          const newToken = res.data?.access_token as string;
          if (newToken) {
            useAuthStore.getState().setToken(newToken);
          }
        })
        .catch(() => {
          // Silently fail; the reactive interceptor or next API call will handle it
        });
    };

    // Refresh every 15 minutes while active
    const interval = setInterval(doRefresh, 15 * 60 * 1000);

    // Also refresh when tab becomes visible (setInterval is paused in background tabs)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        doRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, token]);

  // Fires once right after a fresh OTP login (see Login.tsx's navigate state)
  // — not on every dashboard visit, and not on the trusted-device fast path.
  React.useEffect(() => {
    const state = location.state as { promptTrustDevice?: boolean } | null;
    if (!state?.promptTrustDevice) return;
    navigate(location.pathname, { replace: true, state: null });

    toast((t) => (
      <div className="flex flex-col gap-2">
        <div>
          <p className="font-semibold text-slate-900">Trust this device?</p>
          <p className="text-sm text-slate-600 mt-0.5">
            Skip the OTP step here for 30 days. This will sign out any other device you've trusted.
          </p>
        </div>
        <div className="flex justify-end gap-3 mt-1">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-sm font-medium text-slate-500 hover:text-slate-700"
          >
            Not now
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const { email_sent } = await authAPI.trustDevice();
                if (email_sent) {
                  toast.success('This device is now trusted for 30 days.');
                } else {
                  toast('Device trusted, but the confirmation email failed to send.', { icon: '⚠️' });
                }
              } catch {
                toast.error("Couldn't trust this device — you may be asked for an OTP again next time.");
              }
            }}
            className="text-sm font-semibold text-sky-700 hover:text-sky-900"
          >
            Trust device
          </button>
        </div>
      </div>
    ), { duration: 15000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const isSessionReady = isAuthenticated && !!token && !!user?.email && authBootstrapComplete;
  const isAuraLensEnabled = AI_INTELLIGENCE_ENABLED && Boolean(auraLensRoute) && isSessionReady;
  const { data: activeSummaryPayload, isLoading: isSummaryPayloadLoading, error: summaryPayloadError } = useQuery(
    ['aura-lens-page-summary', location.pathname, auraLensSessionKey],
    () => auraLensRoute!.config.buildPayload({
      pathname: location.pathname,
      params: auraLensRoute!.params,
      viewer: {
        name: user?.username || null,
        role: user?.role || null,
      },
    }),
    {
      enabled: isAuraLensEnabled,
      staleTime: isAuraLensEnabled ? Infinity : 0,
      cacheTime: isAuraLensEnabled ? Infinity : 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
  const { data: auraLensHealth } = useQuery(
    ['aura-lens-page-summary-health', auraLensSessionKey],
    monitorAPI.getAuraLensHealth,
    {
      enabled: isAuraLensEnabled,
      staleTime: isAuraLensEnabled ? 5 * 60 * 1000 : 0,
      cacheTime: isAuraLensEnabled ? 5 * 60 * 1000 : 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  if (isAuthenticated && !authBootstrapComplete) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-3xl border border-gray-200 bg-white px-8 py-6 shadow-sm">
          <div className="flex items-center gap-3 text-slate-700">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <div>
              <div className="text-sm font-semibold">Restoring your session</div>
              <div className="text-xs text-slate-500">Checking authentication before loading protected pages.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* 1. Developer Portal is always a standalone full page, even if not logged in */}
      <Route path="/developer-portal" element={<DeveloperPortal />} />
      
      {/* 2. Swagger Docs - Redirect to backend Swagger UI */}
      <Route 
        path="/docs" 
        element={
          <div style={{ width: '100vw', height: '100vh', border: 'none' }}>
            <iframe
              src={SWAGGER_URL}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Swagger API Documentation"
            />
          </div>
        } 
      />

      {/* 2. Routes for non-authenticated users */}
      {!isAuthenticated ? (
        <>
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/*" element={<Login />} />
        </>
      ) : (
        /* 3. Routes for authenticated users with main layout */
        <Route
          path="/*"
          element={
            <div className="flex min-h-screen bg-gray-50">
              {/* Mobile overlay */}
              {sidebarOpen && (
                <div 
                  className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
                  onClick={() => setSidebarOpen(false)}
                />
              )}
              
              {/* Sidebar for desktop & mobile */}
              <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
              <div className="flex-1 flex flex-col overflow-hidden md:ml-0">
                {/* <Header onToggleSidebar={() => setSidebarOpen(true)} /> */}
                {!sidebarOpen && (
                  <button
                    className="md:hidden fixed top-4 left-4 z-40 bg-white/90 backdrop-blur border border-gray-200 shadow-lg rounded-2xl px-4 py-3 text-gray-700 hover:text-gray-900 transition"
                    onClick={() => setSidebarOpen(true)}
                    aria-label="Open sidebar"
                  >
                    <i className="fas fa-bars fa-lg"></i>
                  </button>
                )}
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6 pb-20 min-h-full">
                  {isAuraLensEnabled ? (
                    <AuraLensPanel
                      sessionKey={auraLensSessionKey}
                      routeKey={location.pathname}
                      payload={activeSummaryPayload}
                      isLoadingPayload={isSummaryPayloadLoading}
                      payloadError={summaryPayloadError ? 'AI Intelligence could not prepare the page briefing right now.' : null}
                      modelReachable={Boolean(auraLensHealth?.reachable)}
                    />
                  ) : null}
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/auth" 
                      element={
                        <ProtectedRoute>
                          <Authentication />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/forms" 
                      element={
                        <ProtectedRoute>
                          <FormsRoute>
                            <Forms />
                          </FormsRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/forms/:id/workspace"
                      element={
                        <ProtectedRoute>
                          <FormsRoute>
                            <FormWorkspace />
                          </FormsRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/sales" 
                      element={
                        <ProtectedRoute>
                          <SalesRoute>
                            <Sales />
                          </SalesRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/usage" 
                      element={
                        <ProtectedRoute>
                          <DataUsage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/usage/exports"
                      element={
                        <ProtectedRoute>
                          <DataUsageExports />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/migrations" 
                      element={
                        <SuperAdminRoute>
                          <Migrations />
                        </SuperAdminRoute>
                      } 
                    />
                    <Route
                      path="/migrations/history"
                      element={
                        <SuperAdminRoute>
                          <MigrationHistory />
                        </SuperAdminRoute>
                      }
                    />
                    <Route 
                      path="/users" 
                      element={
                        <AdminRoute>
                          <Users />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/monitor" 
                      element={
                        <ProtectedRoute>
                          <MonitorRoute>
                            <StarlinkMonitor />
                          </MonitorRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/packages" 
                      element={
                        <AdminRoute>
                          <Packages />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/audit" 
                      element={
                        <AdminRoute>
                          <AuditTrail />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/powerbi" 
                      element={
                        <ProtectedRoute>
                          <PowerBI />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/xero" 
                      element={
                        <ProtectedRoute>
                          <Xero />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/email-management" 
                      element={
                        <ProtectedRoute>
                          <AdminRoute>
                            <EmailManagement />
                          </AdminRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/login-audits" 
                      element={
                        <ProtectedRoute>
                          <AdminRoute>
                            <LoginAudits />
                          </AdminRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/usage-100-percent" 
                      element={
                        <ProtectedRoute>
                          <Usage100Percent />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/data-transfer" 
                      element={
                        <ProtectedRoute>
                          <DataTransferRoute>
                            <DataTransfer />
                          </DataTransferRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/impact-plan" 
                      element={
                        <ProtectedRoute>
                          <ImpactPlanRoute>
                            <ImpactPlan />
                          </ImpactPlanRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/impact-plan/details" 
                      element={
                        <ProtectedRoute>
                          <ImpactPlanRoute>
                            <ImpactPlanDetails />
                          </ImpactPlanRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/service-accounts" 
                      element={
                        <AdminRoute>
                          <ServiceAccounts />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="/usage-events" 
                      element={
                        <ProtectedRoute>
                          <AdminRoute>
                            <UsageEvents />
                          </AdminRoute>
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/login" element={<Login />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </main>
              </div>
            </div>
          }
        />
      )}
    </Routes>
  );
};

export default App;
